import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../api/client';
import type { SourceKind } from '../api/bindings/SourceKind';

/** Human "polled 5m ago" hint so the user can see the RSS rail actually working. */
function pollStatus(iso: string | null): string {
  if (!iso) return 'never polled';
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  if (secs < 60) return 'polled just now';
  if (secs < 3600) return `polled ${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `polled ${Math.floor(secs / 3600)}h ago`;
  return `polled ${Math.floor(secs / 86400)}d ago`;
}

export function Sources() {
  const qc = useQueryClient();
  const sources = useQuery({ queryKey: ['sources'], queryFn: api.listSources });

  const [kind, setKind] = useState<SourceKind>('rss');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [weight, setWeight] = useState(1);
  const fileInput = useRef<HTMLInputElement>(null);

  // Preview of the feed a pasted URL resolves to (populated by "Find feed").
  const discover = useMutation({
    mutationFn: () => api.discoverFeed(url),
    onSuccess: (d) => {
      if (d.title && !name.trim()) setName(d.title);
    },
  });

  const create = useMutation({
    // Send the resolved feed URL when we have one (fast path); otherwise the raw URL,
    // which the server resolves itself. Agentic sources carry no URL.
    mutationFn: () =>
      api.createSource({
        kind,
        name,
        url: kind === 'rss' ? (discover.data?.feed_url ?? url) : null,
        weight,
      }),
    onSuccess: () => {
      setName('');
      setUrl('');
      setWeight(1);
      discover.reset();
      qc.invalidateQueries({ queryKey: ['sources'] });
    },
  });

  const importOpml = useMutation({
    mutationFn: (opml: string) => api.importOpml(opml),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sources'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteSource(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sources'] }),
  });

  // Editing the URL invalidates a previous discovery preview.
  const onUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    if (discover.data || discover.error) discover.reset();
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-importing the same file
    if (file) importOpml.mutate(await file.text());
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  const errOf = (e: unknown) => (e instanceof ApiError ? e.message : e ? String(e) : null);
  const createError = errOf(create.error);
  const discoverError = errOf(discover.error);
  const importError = errOf(importOpml.error);
  const summary = importOpml.data;

  return (
    <section>
      <header className="page-head">
        <h1>Sources</h1>
      </header>

      <form className="card" onSubmit={onSubmit}>
        <div className="row">
          <label>
            Kind
            <select value={kind} onChange={(e) => setKind(e.target.value as SourceKind)}>
              <option value="rss">RSS / Atom feed</option>
              <option value="agentic">Agentic (pushed)</option>
            </select>
          </label>
          <label className="grow">
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Hacker News" required />
          </label>
        </div>

        {kind === 'rss' && (
          <>
            <label>
              Page or feed URL
              <div className="row">
                <input
                  className="grow"
                  type="url"
                  value={url}
                  onChange={onUrlChange}
                  placeholder="https://example.com — a homepage, YouTube channel, subreddit, or feed"
                  required
                />
                <button
                  type="button"
                  className="ghost"
                  disabled={!url || discover.isPending}
                  onClick={() => discover.mutate()}
                >
                  {discover.isPending ? 'Finding…' : 'Find feed'}
                </button>
              </div>
            </label>

            {discover.data && (
              <p className="muted small">
                Found feed{discover.data.title ? ` “${discover.data.title}”` : ''}:{' '}
                <code>{discover.data.feed_url}</code>
              </p>
            )}
            {discoverError && <p className="error">{discoverError}</p>}
          </>
        )}

        <label>
          Baseline weight: <strong>{weight.toFixed(2)}</strong>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={weight}
            onChange={(e) => setWeight(Number(e.target.value))}
          />
        </label>

        {createError && <p className="error">{createError}</p>}
        <button type="submit" className="primary" disabled={create.isPending}>
          {create.isPending ? 'Adding…' : 'Add source'}
        </button>
      </form>

      <div className="card">
        <div className="row">
          <div className="grow">
            <strong>Import OPML</strong>
            <p className="muted small">
              Bulk-add feeds from a reader export or a YouTube subscriptions OPML. Duplicates are skipped.
            </p>
          </div>
          <button
            type="button"
            className="ghost"
            disabled={importOpml.isPending}
            onClick={() => fileInput.current?.click()}
          >
            {importOpml.isPending ? 'Importing…' : 'Choose OPML file'}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".opml,.xml,text/xml,text/x-opml"
            hidden
            onChange={onFile}
          />
        </div>
        {summary && (
          <p className="muted small">
            Imported {summary.added} added, {summary.skipped} skipped
            {summary.failed.length > 0 ? `, ${summary.failed.length} failed` : ''}.
          </p>
        )}
        {summary && summary.failed.length > 0 && (
          <ul className="muted small">
            {summary.failed.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        )}
        {importError && <p className="error">{importError}</p>}
      </div>

      <ul className="list">
        {sources.data?.map((s) => (
          <li key={s.id} className="card list-row">
            <div>
              <span className={`pill pill-${s.kind}`}>{s.kind}</span>
              <strong>{s.name}</strong>
              {s.url && (
                <a className="muted small" href={s.url} target="_blank" rel="noreferrer">
                  {s.url}
                </a>
              )}
            </div>
            <div className="list-row-end">
              {s.kind === 'rss' && <span className="muted small">{pollStatus(s.last_polled_at)}</span>}
              <span className="muted small">w {s.weight.toFixed(2)}</span>
              <button className="ghost" onClick={() => remove.mutate(s.id)}>
                Delete
              </button>
            </div>
          </li>
        ))}
        {sources.data?.length === 0 && <p className="muted empty">No sources yet.</p>}
      </ul>
    </section>
  );
}
